import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@workspace/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@workspace/ui/components/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@workspace/ui/components/form';
import { Input } from '@workspace/ui/components/input';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { m } from '~/paraglide/messages.js';
import { useUpdateAccountName } from '../../-hooks/use-update-account-name';

const editCardNameSchema = z.object({
  name: z.string().min(1, m['cards.edit_validation_required']()),
});

type EditCardNameForm = z.infer<typeof editCardNameSchema>;

interface EditCardNameDialogProps {
  isOpen: boolean;
  onClose: () => void;
  cardUrn: string;
  currentName?: string;
}

export function EditCardNameDialog({
  isOpen,
  onClose,
  cardUrn,
  currentName,
}: EditCardNameDialogProps) {
  const updateCardName = useUpdateAccountName();

  const form = useForm<EditCardNameForm>({
    resolver: zodResolver(editCardNameSchema),
    defaultValues: {
      name: currentName || '',
    },
  });

  const onSubmit = async (data: EditCardNameForm) => {
    updateCardName.mutate(
      {
        urn: cardUrn,
        name: data.name,
      },
      {
        onSuccess: () => {
          onClose();
        },
      },
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{m['cards.edit_dialog_title']()}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{m['cards.edit_name_label']()}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={m['cards.edit_name_placeholder']()}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={updateCardName.isPending}
              >
                {m['common.cancel']()}
              </Button>
              <Button type="submit" disabled={updateCardName.isPending}>
                {updateCardName.isPending
                  ? m['common.saving']()
                  : m['common.save']()}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
